import React, { useEffect, useMemo } from 'react';
import { useLatest } from 'ahooks';
import cx from './index.module.scss';

export interface FrameAnimationProps {
  /** 图片的URL路径 */
  url: string;
  /** 单帧图片数量 */
  count: number;
  /** 图片宽度 */
  width: number;
  /** 图片纵横比 */
  aspectRatio: number;
  /** 动画持续时间 */
  duration: number;
  /** 动画循环次数 */
  loopTimes?: number | 'infinite';
  /** 控制动画是否开始执行 */
  isPlay?: boolean;
  /** 自定义类名 */
  className?: string;
  /** 源文件图片的排列方向 */
  picDirection?: 'row' | 'column';
  /** 动画次数不为无限循环时动画执行完成时的回调 */
  onAnimationComplete?: () => void;
  /** 当动画单次播放完成 */
  onSinglePlayComplete?: () => void;
}

const FrameAnimation: React.FC<FrameAnimationProps> = props => {
  const {
    count,
    width,
    aspectRatio,
    duration,
    onAnimationComplete,
    onSinglePlayComplete,
    className = '',
    url = '',
    isPlay = true,
    loopTimes = 'infinite',
    picDirection = 'row',
  } = props;
  const height = Math.round(width / aspectRatio);
  // 修复动画如果不是无限循环时，动画结束后无法停留在最后一帧的问题
  const fixCount = loopTimes === 'infinite' ? count : count - 1;
  const latestSinglePlayComplete = useLatest(onSinglePlayComplete);

  const styleVariable = useMemo(
    () =>
      ({
        '--url': `url(${url})`,
        '--width': `${width}px`,
        '--height': `${height}px`,
        '--duration': `${duration}`,
        '--count': `${count}`,
        '--fix-count': `${fixCount}`,
        '--animation-play-state': `${isPlay ? 'running' : 'paused'}`,
        '--loop-times': `${loopTimes}`,
        '--animation-keyframes': picDirection === 'row' ? cx['animationRow'] : cx['animationColumn'],
        '--background-size':
          picDirection === 'row' ? `${width * count}px ${height}px` : `${width}px ${count * height}px`,
      } as React.CSSProperties),
    [count, duration, fixCount, height, isPlay, loopTimes, picDirection, url, width]
  );

  useEffect(() => {
    let timerId: NodeJS.Timeout;
    if (onAnimationComplete && isPlay && loopTimes !== 'infinite') {
      timerId = setTimeout(() => {
        onAnimationComplete();
      }, duration * loopTimes * 1000);
    }
    return () => {
      timerId && clearTimeout(timerId);
    };
  }, [loopTimes, isPlay, duration, onAnimationComplete]);

  useEffect(() => {
    if (!latestSinglePlayComplete.current) return;

    let timer = null;

    timer = setInterval(() => latestSinglePlayComplete.current?.(), duration * 1000);

    return () => {
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration]);

  return <div className={`${cx['frame-animation-wrap']} ${className}`} style={styleVariable} />;
};

export default FrameAnimation;
